import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useOutletContext } from 'react-router-dom';
import api from '../services/api';
import { BACKEND_URL } from '../config';
import useWorkspaceBasePath from '../hooks/useWorkspaceBasePath';

const BusinessDeliveryNoteView = ({ projectMode = false }) => {
  const outletContext = useOutletContext();
  const projectId = projectMode ? outletContext?.projectId : null;
  const apiBase = projectMode && projectId ? `/projects/${projectId}/workspace` : '/business';
  const routeBase = useWorkspaceBasePath();

  const { id } = useParams();
  const printRef = useRef();
  const [note, setNote] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editItems, setEditItems] = useState([]);
  const [editForm, setEditForm] = useState({ livreur: '', notes: '', date_livraison: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadData(); }, [id]);

  const loadData = async () => {
    try {
      const [noteRes, setRes] = await Promise.all([
        api.get(`${apiBase}/delivery-notes/${id}`),
        api.get(`${apiBase}/settings`)
      ]);
      setNote(noteRes.data);
      setSettings(setRes.data);
      setEditItems(noteRes.data.items?.map(i => ({ ...i, quantite_livree: i.quantite_livree })) || []);
      setEditForm({
        livreur: noteRes.data.livreur || '',
        notes: noteRes.data.notes || '',
        date_livraison: noteRes.data.date_livraison || ''
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    const originalContents = document.body.innerHTML;
    document.body.innerHTML = printContent.innerHTML;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`${apiBase}/delivery-notes/${id}`, {
        ...editForm,
        items: editItems.map(i => ({
          id: i.id,
          quantite_livree: i.quantite_livree
        }))
      });
      setEditing(false);
      loadData();
    } catch (err) {
      console.error(err);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleValidate = async () => {
    // Vérifier s'il y a des écarts
    const hasDiscrepancy = editItems.some(i => parseFloat(i.quantite_livree) < parseFloat(i.quantite_prevue));
    let msg = 'Valider ce bon de livraison ?\n\nCette action confirme la livraison.';
    if (hasDiscrepancy) {
      msg += '\n\n⚠️ Des écarts de quantité ont été détectés.\nUn avoir sera automatiquement créé pour la différence et le stock sera ajusté.';
    }
    if (!window.confirm(msg)) return;

    // Sauvegarder d'abord les quantités livrées si en mode édition
    if (editing) {
      await handleSave();
    }

    setSaving(true);
    try {
      const res = await api.post(`${apiBase}/delivery-notes/${id}/validate`);
      if (res.data.has_discrepancy) {
        alert('✅ Bon de livraison validé avec écarts.\n\nUn avoir a été généré automatiquement et le stock a été ajusté.');
      } else {
        alert('✅ Bon de livraison validé avec succès !');
      }
      loadData();
    } catch (err) {
      console.error(err);
      alert('Erreur lors de la validation');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer ce bon de livraison ?')) return;
    try {
      await api.delete(`${apiBase}/delivery-notes/${id}`);
      window.location.href = `${routeBase}/invoices/${note.invoice_id}`;
    } catch (err) {
      console.error(err);
      alert('Erreur lors de la suppression');
    }
  };

  const updateItemQuantity = (index, qty) => {
    const max = parseFloat(editItems[index].quantite_prevue);
    const val = Math.max(0, Math.min(parseFloat(qty) || 0, max));
    setEditItems(prev => prev.map((item, i) => i === index ? { ...item, quantite_livree: val } : item));
  };

  const getStatusBadge = (statut) => {
    const map = {
      'en_preparation': { bg: 'bg-amber-100', text: 'text-amber-700', label: '📦 En préparation' },
      'livre': { bg: 'bg-green-100', text: 'text-green-700', label: '✅ Livré' },
      'livre_partiel': { bg: 'bg-orange-100', text: 'text-orange-700', label: '⚠️ Livré partiellement' },
    };
    const s = map[statut] || { bg: 'bg-gray-100', text: 'text-gray-700', label: statut };
    return <span className={`px-3 py-1 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>{s.label}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!note) {
    return (
      <div className="max-w-3xl mx-auto p-6 text-center">
        <h2 className="text-xl font-bold text-gray-700">Bon de livraison non trouvé</h2>
        <Link to={`${routeBase}/invoices`} className="text-indigo-600 hover:underline mt-4 inline-block">← Retour</Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-4 lg:p-6">
      {/* Actions header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 print:hidden">
        <Link to={`${routeBase}/invoices/${note.invoice_id}`} className="flex items-center gap-2 text-gray-500 hover:text-gray-700">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Retour à la facture
        </Link>
        <div className="flex items-center gap-3">
          {note.statut === 'en_preparation' && !editing && (
            <>
              <button onClick={() => setEditing(true)} className="px-4 py-2 bg-amber-100 text-amber-600 rounded-lg hover:bg-amber-200 transition">
                ✏️ Modifier les quantités
              </button>
              <button onClick={handleValidate} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition">
                ✅ Valider la livraison
              </button>
              <button onClick={handleDelete} className="px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition">
                🗑️
              </button>
            </>
          )}
          {editing && (
            <>
              <button onClick={() => { setEditing(false); loadData(); }} className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition">
                Annuler
              </button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition disabled:opacity-50">
                {saving ? 'Enregistrement...' : '💾 Enregistrer'}
              </button>
              <button onClick={handleValidate} disabled={saving} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50">
                ✅ Enregistrer & Valider
              </button>
            </>
          )}
          <button onClick={handlePrint} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition">
            🖨️ Imprimer
          </button>
        </div>
      </div>

      {/* Editing form for livreur/notes */}
      {editing && (
        <div className="bg-amber-50 rounded-2xl border border-amber-200 p-6 mb-6 print:hidden">
          <h3 className="font-bold text-amber-800 mb-4">Modifier le bon de livraison</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Livreur</label>
              <input type="text" value={editForm.livreur} onChange={(e) => setEditForm({...editForm, livreur: e.target.value})}
                placeholder="Nom du livreur" className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-amber-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date de livraison</label>
              <input type="date" value={editForm.date_livraison} onChange={(e) => setEditForm({...editForm, date_livraison: e.target.value})}
                className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-amber-500" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea value={editForm.notes} onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                rows={2} placeholder="Notes de livraison..." className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-amber-500" />
            </div>
          </div>
        </div>
      )}

      {/* Printable content */}
      <div ref={printRef}>
        <style dangerouslySetInnerHTML={{ __html: `
          @media print {
            @page { size: A4; margin: 15mm; }
            body { print-color-adjust: exact; -webkit-print-color-adjust: exact; font-family: system-ui, sans-serif; }
          }
        `}} />

        <div className="bg-white rounded-2xl shadow-sm border p-8">
          {/* Header entreprise */}
          <div className="flex justify-between items-start mb-8 pb-6 border-b">
            <div>
              {settings?.logo && (
                <img src={`${BACKEND_URL}/${settings.logo}`} alt="Logo" className="h-16 mb-3 object-contain" />
              )}
              <h2 className="text-xl font-bold text-gray-800">{settings?.nom_entreprise || 'Mon Entreprise'}</h2>
              {settings?.adresse && <p className="text-sm text-gray-500">{settings.adresse}</p>}
              {settings?.telephone && <p className="text-sm text-gray-500">Tél: {settings.telephone}</p>}
              {settings?.email && <p className="text-sm text-gray-500">{settings.email}</p>}
              {settings?.numero_registre && <p className="text-sm text-gray-500">RCCM: {settings.numero_registre}</p>}
              {settings?.numero_fiscal && <p className="text-sm text-gray-500">NIF: {settings.numero_fiscal}</p>}
            </div>
            <div className="text-right">
              <h1 className="text-3xl font-bold text-indigo-600">BON DE LIVRAISON</h1>
              <p className="text-lg font-semibold text-gray-700 mt-1">{note.numero_bl}</p>
              <p className="text-sm text-gray-500 mt-2">Date : {new Date(note.date_livraison).toLocaleDateString('fr-FR')}</p>
              <p className="text-sm text-gray-500">Facture : {note.numero_facture}</p>
              <div className="mt-2">{getStatusBadge(note.statut)}</div>
            </div>
          </div>

          {/* Client + Livreur */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
            {note.client_nom && (
              <div className="p-4 bg-gray-50 rounded-xl">
                <h3 className="text-sm font-semibold text-gray-500 mb-1">CLIENT / DESTINATAIRE</h3>
                <p className="font-bold text-gray-800">{note.client_nom}</p>
                {note.client_adresse && <p className="text-sm text-gray-600">{note.client_adresse}</p>}
                {note.client_telephone && <p className="text-sm text-gray-600">Tél: {note.client_telephone}</p>}
              </div>
            )}
            {note.livreur && (
              <div className="p-4 bg-blue-50 rounded-xl">
                <h3 className="text-sm font-semibold text-blue-600 mb-1">LIVREUR</h3>
                <p className="font-bold text-gray-800">{note.livreur}</p>
              </div>
            )}
          </div>

          {/* Tableau articles */}
          <table className="w-full mb-6">
            <thead>
              <tr className="bg-gray-100">
                <th className="text-left p-3 text-sm font-semibold text-gray-600 rounded-tl-lg">Description</th>
                <th className="text-center p-3 text-sm font-semibold text-gray-600">Qté prévue</th>
                <th className="text-center p-3 text-sm font-semibold text-gray-600">Qté livrée</th>
                <th className="text-center p-3 text-sm font-semibold text-gray-600 rounded-tr-lg">Écart</th>
              </tr>
            </thead>
            <tbody>
              {(editing ? editItems : note.items)?.map((item, idx) => {
                const ecart = parseFloat(item.quantite_prevue) - parseFloat(item.quantite_livree);
                return (
                  <tr key={idx} className={`border-b border-gray-100 ${ecart > 0 ? 'bg-red-50' : ''}`}>
                    <td className="p-3 text-gray-800">
                      <div className="font-medium">{item.description}</div>
                      {item.code_produit && <div className="text-xs text-gray-400">Code: {item.code_produit}</div>}
                    </td>
                    <td className="p-3 text-center text-gray-700">{item.quantite_prevue}</td>
                    <td className="p-3 text-center">
                      {editing ? (
                        <input
                          type="number"
                          value={item.quantite_livree}
                          onChange={(e) => updateItemQuantity(idx, e.target.value)}
                          min="0"
                          max={item.quantite_prevue}
                          className="w-20 text-center border rounded-lg py-1 focus:ring-2 focus:ring-indigo-500"
                        />
                      ) : (
                        <span className={ecart > 0 ? 'text-red-600 font-semibold' : 'text-green-600 font-semibold'}>
                          {item.quantite_livree}
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-center">
                      {ecart > 0 ? (
                        <span className="text-red-600 font-bold">-{ecart}</span>
                      ) : (
                        <span className="text-green-600">✓</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Notes */}
          {note.notes && (
            <div className="p-4 bg-gray-50 rounded-xl mb-6">
              <h3 className="text-sm font-semibold text-gray-500 mb-1">NOTES</h3>
              <p className="text-sm text-gray-600 whitespace-pre-line">{note.notes}</p>
            </div>
          )}

          {/* Bloc signature */}
          <div className="mt-8 pt-6 border-t">
            <div className="grid grid-cols-3 gap-8">
              <div>
                <p className="text-sm font-semibold text-gray-500 mb-8">Préparé par</p>
                <div className="border-b border-gray-300 w-full"></div>
                <p className="text-xs text-gray-400 mt-1">Nom et signature</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500 mb-8">Livré par</p>
                <div className="border-b border-gray-300 w-full"></div>
                <p className="text-xs text-gray-400 mt-1">{note.livreur || 'Nom et signature'}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500 mb-8">Reçu par le client</p>
                <div className="border-b border-gray-300 w-full"></div>
                <p className="text-xs text-gray-400 mt-1">Nom, signature et date</p>
              </div>
            </div>
            {note.signature_client && (
              <div className="mt-4 text-center">
                <span className="inline-flex items-center gap-1 text-green-600 text-sm font-medium">
                  ✅ Réception confirmée
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lien vers la facture */}
      <div className="mt-6 text-center print:hidden">
        <Link to={`${routeBase}/invoices/${note.invoice_id}`} className="text-indigo-600 hover:underline">
          📄 Voir la facture {note.numero_facture}
        </Link>
      </div>
    </div>
  );
};

export default BusinessDeliveryNoteView;
